import { defineStore } from 'pinia'

import {
  extractAllUserGroups,
  findHighestUserLevel,
} from '~/utils/auth/parseUserLevel.js'

// /*
//  * User store
//  * Holds details of the signed-in user (or null if not signed in).
//  * Important: you cannot trust anything in this store. Don't use it
//  * for authorization on the client side. The expected use of the
//  * store is to decide whether to display a link to the login page,
//  * whether to display a link to the digital collection page, etc.
//  * But when the user visits the digital collection page, we must
//  * hit the server middleware (/api) to retrieve the authorized
//  * content for that page.
//  */

import type { ITabletopUser, ITabletopOrganization } from '~/types/tabletop'

export interface State {
  personaId: string | undefined
  accountId: string | undefined
  displayName: string
  loggedIn: boolean
  rolesRetrieved: boolean
  roles: ITabletopUser['roles']
  stores: ITabletopOrganization[]
  gaUserId: string | undefined
}

export const useUserStore = defineStore('user', {
  state: (): State => ({
    personaId: '',
    accountId: '',
    displayName: '',
    loggedIn: false,
    rolesRetrieved: false,
    roles: [],
    stores: [],
    gaUserId: undefined,
  }),
  getters: {
    highestUserLevel({ roles, stores }) {
      // Users without any stores are capped at access level 0
      if (!stores?.length) return 0

      const allGroups = extractAllUserGroups(roles)
      return findHighestUserLevel(allGroups)
    },
  },
  actions: {
    setUser({
      personaId,
      accountId,
      loggedIn,
      displayName,
      roles,
      rolesRetrieved,
      stores,
      gaUserId,
    }: {
      personaId: string | undefined
      accountId: string | undefined
      loggedIn: boolean
      displayName: string
      rolesRetrieved: boolean
      roles: ITabletopUser['roles']
      stores: ITabletopOrganization[]
      gaUserId?: string | undefined
    }) {
      this.personaId = personaId
      this.accountId = accountId
      this.displayName = displayName
      this.loggedIn = loggedIn
      this.roles = roles
      this.rolesRetrieved = rolesRetrieved
      this.stores = stores
      this.gaUserId = gaUserId
    },
  },
})
